import React from 'react';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import SyncIcon from '@mui/icons-material/Sync';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Stage render states. `skipped` = genuinely not reached (gray, benign);
// `unknown` = we could not read what happened (distinct, never benign).
export type StageState = 'skipped' | 'running' | 'success' | 'warning' | 'failed' | 'unknown';

export interface StageStep {
  key: string;
  label: string;
  state: StageState;
  /** Omitted when the stage's own icon says everything there is to say. */
  note?: string;
  /**
   * Icon for the success state. `observed` is for a stage that has no
   * "succeeded" semantics of its own and can only report that it ran.
   */
  successIcon?: 'check' | 'observed';
}

function stateColor(state: StageState, theme: Theme): string {
  switch (state) {
    case 'running':
      return theme.palette.info.main; // blue
    case 'success':
      return theme.palette.success.main; // green
    case 'warning':
      return theme.palette.warning.main; // yellow
    case 'failed':
      return theme.palette.error.main; // red
    case 'unknown':
      return theme.palette.text.secondary; // distinct from skipped's disabled gray
    case 'skipped':
    default:
      return theme.palette.text.disabled; // gray
  }
}

const StageIcon: React.FC<Pick<StageStep, 'state' | 'successIcon'>> = ({ state, successIcon }) => {
  const sx = { fontSize: 18, color: (t: Theme) => stateColor(state, t) };
  if (state === 'running') return <SyncIcon sx={sx} />;
  if (state === 'failed') return <ErrorIcon sx={sx} />;
  if (state === 'warning') return <WarningIcon sx={sx} />;
  if (state === 'unknown') return <HelpOutlineIcon sx={sx} />;
  if (state === 'success') {
    return successIcon === 'observed' ? <VisibilityIcon sx={sx} /> : <CheckCircleIcon sx={sx} />;
  }
  return <RadioButtonUncheckedIcon sx={sx} />;
};

export interface StageStepperProps {
  /** The stages to render, in order. Nothing renders for an empty list. */
  steps: StageStep[];
  /** Names the sequence for a screen reader, e.g. "Reconciler pipeline phases". */
  ariaLabel: string;
}

/**
 * A horizontal run of stages, each with an icon, a label and a one-line note.
 * Purely presentational: it renders the steps it is handed and derives nothing,
 * so each caller owns the vocabulary of its own stages.
 */
const StageStepper: React.FC<StageStepperProps> = ({ steps, ariaLabel }) => {
  if (steps.length === 0) return null;

  return (
    <Box
      role="list"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        // Stages divide the width between them, so on a phone three of them
        // crush their labels to a word each. Below `sm` they stack instead.
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'stretch' },
        gap: { xs: 1, sm: 0 },
        py: 1,
      }}
    >
      {steps.map((step, i) => (
        <React.Fragment key={step.key}>
          {i > 0 && (
            <Box
              aria-hidden
              sx={{
                // The connector joins stages laid out in a row; stacked, it
                // would be a stray dash under each one.
                display: { xs: 'none', sm: 'block' },
                flex: '0 0 16px',
                alignSelf: 'flex-start',
                mt: '8px',
                height: 2,
                bgcolor: 'divider',
              }}
            />
          )}
          <Box
            role="listitem"
            sx={{
              flex: { xs: '0 0 auto', sm: 1 },
              alignSelf: { xs: 'stretch', sm: 'auto' },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StageIcon state={step.state} successIcon={step.successIcon} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {step.label}
              </Typography>
            </Box>
            {step.note && (
              <Typography
                variant="caption"
                color={step.state === 'skipped' ? 'text.disabled' : 'text.secondary'}
                sx={{ display: 'block', lineHeight: 1.3 }}
              >
                {step.note}
              </Typography>
            )}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default StageStepper;
